import { ToDo } from '@/shared/components'
import { localStorageInstallationKey } from '@/shared/config/config'
import {
  areMandatoryInfoToCreateInstallationCompleted,
  useInstallations
} from '@/features/installations/hooks/useInstallations'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'
import useI18n from '@/shared/hooks/useI18n'
import { useSession } from '@/shared/hooks/useSession'
import { useMemo } from 'react'
import { Trans } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

export default function InstallationSetupPage() {
  const { t } = useI18n()
  const navigate = useNavigate()

  const organization = useSession().session?.organization
  const installationQuery = useInstallations()
  const installations = installationQuery.data ?? []

  const doesOrganizationRequiresLocalAddress = useDoesOrganizationRequiresLocalAddress(
    organization?.placeOfRegistration
  )

  const isOrganizationDetailsCompleted = organization
    ? areMandatoryInfoToCreateInstallationCompleted(
        organization,
        organization.country,
        doesOrganizationRequiresLocalAddress
      )
    : false
  const isInstallationSetupComplete = isOrganizationDetailsCompleted && installations.length > 0

  const steps = useMemo(
    () => [
      {
        id: 'organization-details',
        title: t('installationSetup:steps.organizationDetails.title'),
        actionButton: t('installationSetup:steps.organizationDetails.actionButton'),
        content: (
          <Trans
            t={t}
            i18nKey="installationSetup:steps.organizationDetails.description"
            components={{ red: <span className="text-red-500" /> }}
          />
        ),
        isCompleted: isOrganizationDetailsCompleted,
        action: () => {
          navigate('/settings/organization-details')
        }
      },
      {
        id: 'create-installation',
        title: t('installationSetup:steps.createInstallation.title'),
        actionButton: t('installationSetup:steps.createInstallation.actionButton'),
        disabled: !isOrganizationDetailsCompleted,
        isCompleted: isInstallationSetupComplete,
        content: (
          <Trans
            t={t}
            i18nKey="installationSetup:steps.createInstallation.description"
            components={{ red: <span className="text-red-500" /> }}
          />
        ),
        action: () => {
          window.localStorage.removeItem(localStorageInstallationKey)
          navigate('/installations/new')
        }
      }
    ],
    [navigate, isOrganizationDetailsCompleted, isInstallationSetupComplete, t]
  )

  return (
    <div className="px-14 py-5 flex flex-col items-center h-screen">
      <div className="flex flex-col items-center justify-center space-y-4 h-1/3">
        <h1 className="font-bold text-xl">{t('installationSetup:title')}</h1>
        <p className="text-text-secondary">{t('installationSetup:description')}</p>
      </div>
      <ToDo items={steps} title={t('installationSetup:steps.title')} />
    </div>
  )
}
